import React from "react"
import {Box, Chip, Link, Typography} from "@mui/material"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import {Link as RouterLink} from "react-router-dom"

import type {IdentifiableCustomer, IdentificationHint, UnidentifiedCandidate} from "../types"

import {HintChips} from "./HintChips"

import {colors} from "@indebted/theme/colors"

const strategyLabels: Record<string, string> = {
	SenderAddress: "Sender address",
	SenderPhone: "Sender number",
	ThreadCustomer: "Earlier message in thread",
}

const NO_DOB = "Not available"

function candidateCustomer(candidate: UnidentifiedCandidate): IdentifiableCustomer {
	return {
		CustomerID: candidate.CustomerID,
		Name: candidate.Name,
		Client: candidate.Client,
		CustomerRef: candidate.CustomerRef,
		DOB: candidate.DOB,
		Locality: candidate.Locality,
		MatchedOn: strategyLabels[candidate.Strategy] ?? candidate.Strategy,
	}
}

function dobLabel(dob?: string): string {
	if (!dob) return ""

	return dob === NO_DOB ? "No date of birth on file" : `DOB ${dob}`
}

// The name opens the customer in a new tab: checking a record is how the agent
// tells two same-named customers apart, and it cannot cost them this page.
function CustomerName({customer}: {customer: IdentifiableCustomer}) {
	return (
		<Link
			component={RouterLink}
			to={`/customers/${customer.CustomerID}`}
			target="_blank"
			rel="noopener"
			underline="hover"
			aria-label={`Open ${customer.Name} in a new tab`}
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0.5,
				color: colors.gray["900"],
				fontWeight: 600,
				fontSize: "0.9375rem",
				"&:hover": {color: "primary.main"},
			}}
		>
			{customer.Name}
			<OpenInNewIcon sx={{fontSize: 14, color: colors.gray["400"]}} />
		</Link>
	)
}

// The reference gets its own line: a client's is often a UUID, which would wrap
// the scan line and break mid-string where an agent is matching it character by
// character against what the customer quoted.
function CustomerFacts({customer}: {customer: IdentifiableCustomer}) {
	const facts = [customer.Client, dobLabel(customer.DOB), customer.Locality].filter(Boolean).join(" · ")

	return (
		<Box display="flex" flexDirection="column" gap={0.25}>
			<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
				<Typography variant="body2" color="textSecondary">
					{facts}
				</Typography>
				{customer.MatchedOn && (
					<Chip label={customer.MatchedOn} size="small" variant="outlined" color="primary" />
				)}
				{customer.SystemOfRecord === "Latitude" && (
					<Chip label="Latitude" size="small" variant="outlined" color="error" />
				)}
				{customer.CollectionHold && (
					<Chip label="Collections on hold" size="small" variant="outlined" color="warning" />
				)}
			</Box>
			{customer.CustomerRef && (
				<Typography variant="caption" color="textSecondary" sx={{wordBreak: "break-all"}}>
					Ref {customer.CustomerRef}
				</Typography>
			)}
		</Box>
	)
}

function CandidateCard({
	customer,
	hints = [],
	actions,
}: {
	customer: IdentifiableCustomer
	hints?: IdentificationHint[]
	actions?: React.ReactNode
}) {
	return (
		<Box
			px={2.5}
			py={2}
			display="flex"
			alignItems="center"
			justifyContent="space-between"
			gap={2}
			borderRadius={2}
			sx={{
				border: "1px solid",
				borderColor: colors.gray["200"],
				bgcolor: "background.paper",
				transition: "border-color 120ms",
				"&:hover": {borderColor: colors.gray["400"]},
			}}
		>
			<Box minWidth={0} display="flex" flexDirection="column" gap={0.5}>
				<CustomerName customer={customer} />
				<CustomerFacts customer={customer} />
				<HintChips hints={hints} />
			</Box>
			<Box flexShrink={0}>{actions}</Box>
		</Box>
	)
}

function CandidateList({
	customers,
	title,
	empty,
	renderActions,
}: {
	customers: IdentifiableCustomer[]
	title: string
	empty: React.ReactNode
	renderActions?: (customer: IdentifiableCustomer) => React.ReactNode
}) {
	if (customers.length === 0) {
		return (
			<Typography variant="body2" color="textSecondary">
				{empty}
			</Typography>
		)
	}

	return (
		<Box display="flex" flexDirection="column" gap={1.5}>
			<Typography variant="body2" fontWeight={600}>
				{title}
			</Typography>
			{customers.map((customer) => (
				<CandidateCard key={customer.CustomerID} customer={customer} actions={renderActions?.(customer)} />
			))}
		</Box>
	)
}

export {CandidateCard, CandidateList, candidateCustomer}
